import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Card, CardContent, CardActionArea,
  CircularProgress, Alert, Chip, Button, Avatar,
} from '@mui/material';
import {
  CorporateFare as CorporateFareIcon,
  Nfc as NfcIcon,
  People as PeopleIcon,
  CardGiftcard as RewardIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import corporateService, { CorporateClient } from '../../services/corporate.service';
import CorporateModal from './CorporateModal';

const BRAND = '#6233A0';

const CorporateDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<CorporateClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = () => {
    setLoading(true);
    corporateService.getAll()
      .then(setClients)
      .catch(() => setError('Erro ao carregar empresas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const totalAtivos = clients.reduce((s, c) => s + c.funcionarios_ativos, 0);
  const totalBracelets = clients.reduce((s, c) => s + c.bracelets_ativos, 0);

  const planoColor: Record<string, string> = {
    STARTER: '#0D7F9F',
    PRO: '#6233A0',
    ENTERPRISE: '#F59E0B',
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: BRAND, width: 48, height: 48 }}>
            <NfcIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}>Corporate NFC</Typography>
            <Typography variant="body2" color="text.secondary">
              Bracelets NFC para empresas — recompensas mensais na rede Pagali
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#4e2880' } }}
          onClick={() => setModalOpen(true)}
        >
          Nova Empresa
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats strip */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Empresas activas', value: clients.length,    icon: <CorporateFareIcon />, color: BRAND },
          { label: 'Funcionários',     value: totalAtivos,        icon: <PeopleIcon />,        color: '#0D7F9F' },
          { label: 'Bracelets activos',value: totalBracelets,     icon: <NfcIcon />,           color: '#10B981' },
        ].map(s => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color }}>{s.icon}</Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Empresa cards */}
      {clients.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <NfcIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Nenhuma empresa registada</Typography>
          <Typography variant="body2" color="text.disabled" mb={2}>
            Adiciona a primeira empresa para começar a emitir bracelets NFC.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
            Adicionar Empresa
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {clients.map(client => (
            <Grid item xs={12} sm={6} md={4} key={client.id}>
              <Card variant="outlined" sx={{ '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}>
                <CardActionArea onClick={() => navigate(`/corporate/${(client as any).ID ?? client.id}`)}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" fontWeight={700} noWrap sx={{ maxWidth: '70%' }}>
                        {client.nome}
                      </Typography>
                      <Chip
                        label={client.plano}
                        size="small"
                        sx={{ bgcolor: `${planoColor[client.plano]}20`, color: planoColor[client.plano], fontWeight: 700 }}
                      />
                    </Box>
                    {client.contacto_email && (
                      <Typography variant="body2" color="text.secondary" mb={1}>{client.contacto_email}</Typography>
                    )}
                    <Box display="flex" gap={2} mt={1}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PeopleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption">{client.funcionarios_ativos} activos</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <NfcIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption">{client.bracelets_ativos} bracelets</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <TrendingUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption">dia {client.reward_day}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <CorporateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); load(); }}
      />
    </Box>
  );
};

export default CorporateDashboard;
